import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { Language } from '../types/language';

interface ActivationInfo {
  userId: string;
  email: string;
  activated: boolean;
}

const ActivateUser: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});

  const { data: activationInfo, isLoading } = useQuery({
    queryKey: ['activationInfo', token],
    queryFn: async () => {
      const response = await apiClient.get<ActivationInfo>(`/auth/activate/${token}`);
      return response.data;
    },
    enabled: !!token
  });

  const activateMutation = useMutation({
    mutationFn: async (data: { password: string; preferredLanguage: string }) => {
      const response = await apiClient.post(`/auth/activate/${token}`, data);
      return response.data;
    },
    onSuccess: (data) => {
      // Сохраняем токен и перенаправляем на главную страницу
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('auth_user', JSON.stringify(data.user));
      navigate('/companies');
    },
    onError: (error: any) => {
      setErrors({ password: error.response?.data?.message || 'Ошибка активации' });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Валидация
    if (!password || password.length < 6) {
      setErrors({ password: t('auth.passwordMinLength') || 'Пароль должен содержать минимум 6 символов' });
      return;
    }

    if (password !== confirmPassword) {
      setErrors({ confirmPassword: t('auth.passwordsDoNotMatch') || 'Пароли не совпадают' });
      return;
    }

    activateMutation.mutate({
      password,
      preferredLanguage: language || Language.RU
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!activationInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t('auth.activation.invalidToken') || 'Недействительная ссылка активации'}
          </h1>
          <p className="text-gray-600 mb-4">
            {t('auth.activation.tokenExpired') || 'Ссылка активации недействительна или истекла срок действия.'}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('auth.login') || 'Войти'}
          </button>
        </div>
      </div>
    );
  }

  if (activationInfo.activated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t('auth.activation.alreadyActivated') || 'Аккаунт уже активирован'}
          </h1>
          <p className="text-gray-600 mb-4">
            {t('auth.activation.alreadyActivatedMessage') || 'Ваш аккаунт уже активирован. Вы можете войти в систему.'}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('auth.login') || 'Войти'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth.activation.title') || 'Активация аккаунта'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth.activation.subtitle') || 'Приветствуем'} {activationInfo.email}
          </p>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth.activation.description') || 'Установите пароль для завершения активации аккаунта'}
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="password" className="sr-only">
                {t('auth.password') || 'Пароль'}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder={t('auth.password') || 'Пароль'}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>
            <div>
              <label htmlFor="confirmPassword" className="sr-only">
                {t('auth.confirmPassword') || 'Подтвердите пароль'}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder={t('auth.confirmPassword') || 'Подтвердите пароль'}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={activateMutation.isPending}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {activateMutation.isPending 
                ? (t('auth.activation.activating') || 'Активация...') 
                : (t('auth.activation.activate') || 'Активировать аккаунт')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActivateUser;

